import cx from 'classnames/bind';
import getIsInCheckedMode from 'mini-mail-list-store/lib/utils/getIsInCheckedMode';
import getSelectedTableView from 'mini-mail-list-store/lib/utils/getSelectedTableView';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import loadAutomaticRepliesBannerOptions from 'mini-mail-list-store/lib/actions/loadAutomaticRepliesBannerOptions';
import MailList from './MailList';
import mailStore from 'owa-mail-store/lib/store/Store';
import MultiSelectFooter from './MultiSelectFooter';
import React from 'react';
import setMailListFocusInfo from 'mini-mail-list-store/lib/actions/setMailListFocusInfo';
import setMultiSelectPerfDatapoint from 'mini-mail-list-store/lib/actions/multiSelect/setMultiSelectPerfDatapoint';
import styles from './ListPane.scss';
import type TableView from 'mini-mail-list-store/lib/store/schema/TableView';
import updateSelectedIndex from 'owa-mail-store/lib/actions/updateSelectedIndex';
import { canShowUpNext } from '../utils/mailList/canShowUpNext';
import { getDocumentScrollTop, setDocumentScrollTop } from 'dom-utils/lib/utils/scrollingUtils';
import { isActivityPaneShown } from 'mini-activity-pane-store/lib/selectors/isActivityPaneShown';
import { isFullComposeShown } from 'mini-mail-compose-store';
import { isInSearchView } from 'mini-mail-list-store/lib/selectors/isInSearchView';
import { LazyUpNextEventDetailRoutes } from 'mini-mail-list-upnext-view';
import { MAIL_LIST_SCROLLED_3_TIMES } from 'mini-mail-workflow-tracking/lib/constants/ListViewWorkflowConstants';
import { observer } from 'mobx-react';
import { observer as observerLite } from 'mobx-react-lite';
import { signalWorkflowEvent } from 'mini-workflow-manager';
import { trace } from 'owa-trace';
import { UpNextStateContextProvider } from 'mini-upnext/lib/components/UpNextStateContext';
import { useMatch } from 'mini-react-router-dom';

const classNames = cx.bind(styles);

const MAIL_LIST_ITEM_HEIGHT = 80;
const MAIL_LIST_ITEM_HEIGHT_WITHOUT_PREVIEW = 60;

let onMailListScrolling: boolean = false;
let mailListScrolledCount: number = 0;

interface ListPaneProps {
    isListPaneShown: boolean;
}

@observer
export class ListPane extends React.Component<ListPaneProps, {}> {
    private previousIsListPaneShown = true;
    private mailListScrollTop = 0;
    private selectedItemIndex = -1;
    private lastIsInCheckedMode: boolean = false;
    private lastInComposePane: boolean = false;

    private isSelectionModeToggled(tableView: TableView): boolean {
        return tableView.isInCheckedMode !== this.lastIsInCheckedMode;
    }

    private isListPaneShown(): boolean {
        // List pane is shown when reading pane, compose, ActivityPane are  hidden.
        return this.props.isListPaneShown;
    }

    private getCurrentlySelectedIndex(): number {
        const selectedItemId = mailStore.mailModule.selectedId;
        if (selectedItemId) {
            const tableView = getSelectedTableView();
            return tableView.itemIds.indexOf(selectedItemId);
        } else {
            return -1;
        }
    }

    getSnapshotBeforeUpdate() {
        if (
            this.previousIsListPaneShown !== this.isListPaneShown() &&
            this.previousIsListPaneShown
        ) {
            // This is when list pane is about to be hidden,
            // remember its current scroll top and
            // the currently selected index
            this.mailListScrollTop = getDocumentScrollTop();
            this.selectedItemIndex = this.getCurrentlySelectedIndex();
            updateSelectedIndex(this.selectedItemIndex);
        }

        return null;
    }

    componentDidUpdate() {
        const tableView = getSelectedTableView();
        if (
            !this.lastIsInCheckedMode &&
            !!tableView &&
            tableView.isInCheckedMode &&
            tableView.multiSelectPerfDatapoint
        ) {
            tableView.multiSelectPerfDatapoint.end();
            setMultiSelectPerfDatapoint(null);
        }

        if (this.previousIsListPaneShown !== this.isListPaneShown()) {
            this.previousIsListPaneShown = this.isListPaneShown();
            if (this.previousIsListPaneShown) {
                const { ShowPreviewTextInListView } = getUserConfiguration().UserOptions;

                const mailListItemHeight = ShowPreviewTextInListView
                    ? MAIL_LIST_ITEM_HEIGHT
                    : MAIL_LIST_ITEM_HEIGHT_WITHOUT_PREVIEW;

                // This is when list pane is about to be shown,
                // need to restore and adjust scroll top by # of items navigated in RP
                setDocumentScrollTop(
                    this.mailListScrollTop +
                        mailListItemHeight *
                            (mailStore.mailModule.selectedIndex - this.selectedItemIndex)
                );
            }
        }
        this.setFocusOnComponentUpdate();
    }

    componentWillUnmount() {
        setMailListFocusInfo(getSelectedTableView(), {
            shouldFocusInMailList: false,
            focusedRowId: null,
        });
    }

    componentDidMount() {
        this.setFocusOnComponentUpdate();

        // Load the auto reply config to store
        loadAutomaticRepliesBannerOptions();
    }

    private setFocusOnComponentUpdate = () => {
        window.requestAnimationFrame(() => {
            this.trySetFocus();
            this.lastIsInCheckedMode = getIsInCheckedMode();
            this.lastInComposePane = isFullComposeShown();
        });
    };

    private shouldFocusInMailList(tableView: TableView): boolean {
        // Don't set focus if list pane is not being shown or if we are in multi-select mode or
        // when we return from compose pane to list view
        return this.isListPaneShown() && !tableView.isInCheckedMode && !this.lastInComposePane;
    }

    private trySetFocus = () => {
        const tableView = getSelectedTableView();
        if (!tableView) {
            trace.warn('selected tableview is falsy in ListPane.');
            return;
        }

        const shouldFocusInMailList = this.shouldFocusInMailList(tableView);
        let focusedRowId = tableView.focusInfo.focusedRowId;
        if (this.isSelectionModeToggled(tableView)) {
            // Going to and coming back from multi select mode should not focus, this prevents the scroll reset to top
            focusedRowId = null;
        }
        setMailListFocusInfo(tableView, { focusedRowId, shouldFocusInMailList });
    };

    private onMailListScroll = () => {
        onMailListScrolling = true;
    };

    private onMailListTouchEnd = () => {
        if (onMailListScrolling && mailListScrolledCount < 3) {
            mailListScrolledCount++;
            if (mailListScrolledCount === 3) {
                signalWorkflowEvent(MAIL_LIST_SCROLLED_3_TIMES);
            }
        }
    };

    render() {
        const tableView = getSelectedTableView();
        const showUpNext = canShowUpNext();

        return (
            <UpNextStateContextProvider>
                <main
                    aria-hidden={!this.isListPaneShown()}
                    className={classNames(styles.listPane, {
                        [styles.vlvListPaneHidden]: !this.isListPaneShown(),
                        [styles.listPaneLandscape]: !isInSearchView(),
                    })}
                    onScroll={this.onMailListScroll}
                    onTouchEnd={this.onMailListTouchEnd}>
                    <MailList tableView={tableView} isMailListShown={this.isListPaneShown} />

                    {getIsInCheckedMode() && <MultiSelectFooter />}
                </main>

                {showUpNext && <LazyUpNextEventDetailRoutes />}
            </UpNextStateContextProvider>
        );
    }
}

const ListPaneFC: React.FC = () => {
    const eventRouteMatch = useMatch('/:folderId/event/*');
    const isListPaneShown =
        !mailStore.mailModule.selectedId &&
        !isFullComposeShown() &&
        !isActivityPaneShown() &&
        !eventRouteMatch;

    return <ListPane isListPaneShown={isListPaneShown} />;
};

export default observerLite(ListPaneFC);
